import React, { useEffect } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import Script from "next/script";
import { GoogleTagManager } from "@next/third-parties/google";
import { useCookie } from "utils/cookieHelper";
import { IntlProvider } from "react-intl";
import theme from "styles/theme";
import { useLangDetect } from "utils/hooks";
import { allowedLocales, getMessages, normalizeLocale } from "translations";
import { useFeatureFlagCookieSync } from "utils/useFeatureFlagCookieSync";
import { identifyWithPid } from "utils/posthogIdentity";
import { isExcludedRoute } from "lib/excludedRoutes";
import { redactBugsnagEvent, redactPostHogProperties } from "lib/rtsPrivacy";
import { recoverFromChunkError } from "utils/chunkRecovery";
import MetaHead from "components/base/MetaHead";

import "../styles/globals.css";
import "../styles/fonts.css";

// Stable error boundary used before Bugsnag loads. Catches React render errors
// and forwards them to Bugsnag once the SDK is available.
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    import("@bugsnag/js")
      .then(({ default: Bugsnag }) => {
        if (Bugsnag._client) Bugsnag.notify(error);
      })
      .catch(() => {});
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const AppHead = ({ children }) => (
  <MetaHead
    title="LBL_META_KEYWORD_1"
    description="LBL_META_DESCRIPTION"
    imageUrl="/images/meta/image.jpg"
  >
    {children}
  </MetaHead>
);

// Patch for Google Chrome Translate & translation extension DOM mutations.
//
// Why this was added:
// When users browse the website with Google Translate or browser translation extensions enabled,
// the translation engine mutates the live DOM by wrapping text nodes inside <font> tags.
// When React later unmounts or swaps an element during state transitions (e.g. toggling isLoading
// on a submit button, unmounting modals, or swapping view states), React's reconciler calls
// node.parentNode.removeChild(child), expecting node.parentNode to match the fiber's parent DOM node.
// Because the translation engine inserted <font> tags in between, node.parentNode is <font> rather
// than the expected parent element, causing the browser to throw:
// "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
// (or on Safari: "NotFoundError: The object can not be found here.")
//
// By guarding removeChild and insertBefore on Node.prototype, if child.parentNode does not match `this`,
// we safely delegate removal/insertion to the child's actual current parent node, preventing fatal React
// reconciler crashes across the entire site without altering component markup, flexbox layouts, or styles.
if (typeof window !== "undefined") {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        return child.parentNode.removeChild(child);
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (referenceNode.parentNode) {
        return referenceNode.parentNode.insertBefore(newNode, referenceNode);
      }
      return this.appendChild(newNode);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}

function MyApp(props) {
  const { Component, pageProps, router } = props;
  const { locale, defaultLocale, pathname, asPath } = router;
  const cookie = useCookie(props.cookie);
  const cookieLang = cookie.get("NEXT_LOCALE");
  const langDetected = useLangDetect();

  // Sync all PostHog feature flags to cookies
  useFeatureFlagCookieSync();

  // Defer Bugsnag initialisation out of the critical path — loads after first render
  useEffect(() => {
    Promise.all([import("@bugsnag/js"), import("@bugsnag/plugin-react")]).then(
      ([{ default: Bugsnag }, { default: BugsnagPluginReact }]) => {
        if (Bugsnag._client) return;
        Bugsnag.start({
          apiKey: process.env.NEXT_PUBLIC_BUGSNAG_KEY,
          appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
          plugins: [new BugsnagPluginReact()],
          onError: (event) => {
            redactBugsnagEvent(event);
            const error = event.errors[0];
            if (!error) return;
            const { errorClass, errorMessage, stacktrace = [] } = error;
            const topFrame = stacktrace[0];
            const topFile = topFrame?.file ?? "";
            const topCode = Object.values(topFrame?.code ?? {}).join("");

            // User-initiated request cancellation — not a bug
            if (errorClass === "AbortError") return false;

            // Third-party: jQuery expected by SendinBlue/eTriguer scripts, not loaded in our app
            if (
              errorClass === "ReferenceError" &&
              errorMessage === "jQuery is not defined"
            )
              return false;

            // Third-party: inline/injected script DOM-insertion failure (empty file = anonymous script)
            if (errorMessage?.includes("insertBefore") && topFile === "")
              return false;

            // Third-party: Video.js time-tooltip internal width calculation on unmounted player
            if (
              errorClass === "TypeError" &&
              errorMessage?.includes("'width'") &&
              topCode.includes("time-tooltip")
            )
              return false;

            // Network: unhandled fetch rejections are either third-party script failures or user
            // connectivity issues. Our own fetch calls use try/catch so they won't reach here.
            if (errorMessage === "Failed to fetch") return false;

            // Broken HTTP responses: caused by bots, browser extensions, or network truncation
            if (
              errorClass === "SyntaxError" &&
              errorMessage === "Unexpected end of input"
            )
              return false;

            // PostHog rrweb session recorder trying to snapshot cross-origin iframes (video embeds, maps, etc.)
            if (
              errorClass === "SecurityError" &&
              errorMessage?.includes("cross-origin frame")
            )
              return false;

            // Third-party call stack overflow (PostHog rrweb / Tidio / SendinBlue injected scripts).
            // All frames at file "undefined" means the error is in a dynamically-injected 3rd-party script —
            // our own Next.js bundle always shows /_next/static/chunks/... file paths.
            if (
              errorClass === "RangeError" &&
              errorMessage === "Maximum call stack size exceeded." &&
              stacktrace.every(
                (frame) =>
                  !frame.file ||
                  frame.file === "undefined" ||
                  frame.file === "",
              )
            )
              return false;

            // Third-party: Cookie consent / privacy compliance scripts (e.g. OneTrust, Cookiebot) that
            // reference TrackerStorageType — not loaded by our app, fired by bots scanning /privacy-policy.
            if (
              errorClass === "ReferenceError" &&
              errorMessage === "TrackerStorageType is not defined"
            )
              return false;

            // PostHog rrweb session recorder clones DOM nodes for session capture, which can cause React's
            // reconciler to fail with removeChild when the cloned node is no longer in the expected position
            // (triggered by locale switches on blog pages). Not actionable from our code.
            if (
              errorMessage?.includes("removeChild") &&
              stacktrace.some((frame) => frame.file?.includes("posthog"))
            )
              return false;

            // Third-party: Cal.com embed script race condition where doInIframe fires before createIframe
            // completes. Originates entirely within app.cal.com/embed.js — not actionable from our code.
            if (
              stacktrace.some((frame) =>
                frame.file?.includes("cal.com/embed/embed.js"),
              )
            )
              return false;

            // Third-party: Android in-app browser (Instagram/TikTok/Facebook) native
            // bridge destroyed. The host app injects an iabjs performance logger whose
            // postMessage bridge is torn down before the script finishes sending —
            // unfixable injected-script noise and Bugsnag's #1 reported error.
            if (
              errorMessage?.includes("Java object is gone") ||
              errorMessage?.includes("Java exception was raised")
            )
              return false;

            // Third-party: Safari / WebKit native media shadow DOM control artifact.
            // Safari's internal video player scripts read buffered ranges during home
            // video playback; all frames have undefined files and reference
            // EmptyRanges — nothing to do with our bundle.
            if (errorMessage?.includes("EmptyRanges")) return false;
          },
        });
      },
    );
  }, []);

  // Sync NEXT_LOCALE cookie immediately on mount when the URL has an explicit
  // non-default locale. This runs as soon as React hydrates — much earlier than
  // waiting for langDetected — shrinking the race-condition window where the
  // browser might carry a stale cookie into the next server-side request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (locale !== defaultLocale && cookieLang !== locale) {
      cookie.set("NEXT_LOCALE", locale, { expires: 365, path: "/" });
    }
  }, []);

  useEffect(() => {
    if (langDetected != null) {
      if (locale !== defaultLocale) {
        // User is on an explicit non-default locale URL — trust it and sync the cookie
        if (cookieLang !== locale) {
          cookie.set("NEXT_LOCALE", locale, { expires: 365, path: "/" });
        }
        return;
      }
      // On the default locale URL, redirect based on stored preference or browser language
      const lang =
        cookieLang != null
          ? cookieLang
          : (normalizeLocale(langDetected) ?? defaultLocale);
      if (pathname !== "/_error" && pathname !== "/404" && locale !== lang) {
        // Guard against "Invariant: attempted to hard navigate to the same URL":
        // when asPath already carries the target locale prefix (e.g. the user
        // navigated to /pt-br while the router's locale is still catching up),
        // pushing to the same URL with that locale makes Next.js throw the
        // invariant. Skip the redirect in that case.
        const hasLocalePrefix =
          asPath === `/${lang}` ||
          asPath.startsWith(`/${lang}/`) ||
          asPath.startsWith(`/${lang}?`);
        if (!hasLocalePrefix) {
          router.push(asPath, asPath, { locale: lang, shallow: true });
        }
      }
    }
  }, [langDetected]);

  useEffect(() => {
    if (
      router.query &&
      (router.query.utm_campaign ||
        router.query.utm_source ||
        router.query.utm_medium)
    ) {
      cookie.set("utm_campaign", router.query.utm_campaign || "");
      cookie.set("utm_medium", router.query.utm_medium || "");
      cookie.set("utm_source", router.query.utm_source || "");
      cookie.set("utm_term", router.query.utm_term || "");
      cookie.set("utm_content", router.query.utm_content || "");
    }

    return () => {};
  }, [router.query]);

  useEffect(() => {
    const { e, ...query } = router.query;

    if (e) {
      const newQuery = { ...query };

      const newUrl = {
        pathname: router.pathname,
        query: newQuery,
      };

      router.replace(newUrl, undefined, { shallow: true });
    }
  }, [router.query]);

  useEffect(() => {
    // Delay PostHog init until the browser is idle so it doesn't block
    // the main thread during hydration (session recorder adds ~374ms TBT).
    const initPostHog = () => {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        ui_host: "https://eu.posthog.com",
        person_profiles: "always",
        capture_exceptions: process.env.NODE_ENV !== "development",
        enable_recording_console_log: process.env.NODE_ENV !== "development",
        before_send: (event) => {
          redactPostHogProperties(event?.properties);
          const exceptions = event?.properties?.$exception_list;
          if (!exceptions) return event;
          const value = exceptions[0]?.value ?? "";
          const sources = event?.properties?.$exception_sources ?? [];
          // Android WebView Java bridge destroyed / native method exception
          // (unfixable 3rd-party: iabjs injected by Instagram/TikTok/Facebook WebViews)
          if (
            value.includes("Java object is gone") ||
            value.includes("Java exception was raised")
          )
            return null;
          // Safari / WebKit native media shadow DOM control artifact (3rd-party)
          if (value.includes("EmptyRanges")) return null;
          // PostHog capturing CustomEvent as exception (false positive)
          if (event?.properties?.$exception_types?.includes("CustomEvent"))
            return null;
          // PostHog session recording race condition (PostHog SDK internal)
          if (value.includes("session recording is available")) return null;
          // Browser extension / WebView automation driver artifact
          if (value.includes("Object Not Found Matching Id:")) return null;
          // iOS WKWebView native bridge not available (3rd-party WebView context)
          if (value.includes("window.webkit.messageHandlers")) return null;
          // iOS Instagram/Facebook in-app browser autofill bridge injection (3rd-party)
          if (value.includes("_AutofillCallbackHandler")) return null;
          // Third-party CMP/ad-tech scripts (e.g. logicReadAdsPage) injected by privacy compliance
          // bots scanning the site — anonymous inline code, not loaded by our app.
          if (value.includes("is not defined") && sources.length === 0)
            return null;
          // Cal.com embed script race condition — doInIframe called before createIframe completes.
          // Entirely within Cal.com's embed.js, not actionable from our code.
          if (
            value.includes("iframe doesn't exist") &&
            value.includes("createIframe")
          )
            return null;
          // TikTok Pixel module loader tries to fetch /static/identify.js on our domain (doesn't exist).
          // Error originates inside analytics.tiktok.com pixel script, not our webpack bundle.
          if (sources.some((s) => s?.includes("tiktok.com"))) return null;
          // PostHog SDK gzip compression fails on iOS 16 via Web Streams API (PostHog internal bug
          // in posthog-js/src/request.ts — ReadableStream NotReadableError on older WebKit).
          if (value.includes("I/O read operation failed")) return null;
          return event;
        },
        // Enable debug mode in development
        loaded: (posthog) => {
          if (process.env.NODE_ENV === "development") {
            posthog.debug();
            posthog.opt_out_capturing();
          }
          const ojjId = cookie.get("ojj");
          if (ojjId) {
            posthog.register({ ojj: ojjId });
          }
          identifyWithPid(cookie.get("pid"), { ojj: ojjId });
        },
      });
    };

    // Defer PostHog init until the browser is idle so it doesn't block the
    // main thread during hydration (session recorder adds ~374ms TBT).
    let idleCallbackId;
    if ("requestIdleCallback" in window) {
      idleCallbackId = requestIdleCallback(initPostHog, { timeout: 4000 });
    } else {
      idleCallbackId = setTimeout(initPostHog, 3000);
    }

    const handleRouteChange = () => {
      posthog?.capture("$pageview");
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      if ("requestIdleCallback" in window) {
        cancelIdleCallback(idleCallbackId);
      } else {
        clearTimeout(idleCallbackId);
      }
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, []);

  // Auto-recover from ChunkLoadError after new deployments. A tab opened
  // before a deploy references old chunk URLs; navigating then fails with a
  // 404 and fires routeChangeError. A full reload fetches fresh HTML with the
  // new asset manifest. See utils/chunkRecovery.js for the matching rules and
  // the reload-loop guard.
  useEffect(() => {
    const handleRouteChangeError = (err) => {
      recoverFromChunkError(err, () => window.location.reload());
    };

    router.events.on("routeChangeError", handleRouteChangeError);
    return () => {
      router.events.off("routeChangeError", handleRouteChangeError);
    };
  }, [router]);

  // NEXT_PUBLIC_IS_PRODUCTION must be set to "true" only in the
  // www.hillhelicopters.com deployment. All other environments
  // (staging, preview, localhost) get noindex automatically.
  // This works both server-side (SSR) and client-side.
  const isProduction = process.env.NEXT_PUBLIC_IS_PRODUCTION === "true";
  const noIndex = !isProduction || isExcludedRoute(asPath);

  return (
    <AppErrorBoundary>
      <GoogleTagManager gtmId="GTM-TDZHRMM" />
      <IntlProvider
        messages={getMessages(locale)}
        locale={locale === "es-la" ? "es-ar" : locale}
        defaultLocale={defaultLocale}
      >
        <ChakraProvider theme={theme(locale)}>
          <AppHead>
            {noIndex && <meta name="robots" content="noindex, nofollow" />}
          </AppHead>
          <Script
            src="https://code.tidio.co/ljeb8hejikdunoqj1cciekvrjhhrdm7l.js"
            id="tidio-chat"
            strategy="lazyOnload"
          />
          <Script
            id="sendinblue-init"
            strategy="lazyOnload"
            dangerouslySetInnerHTML={{
              __html: `(function() {
                window.sib = {
                    equeue: [],
                    client_key: "${process.env.NEXT_PUBLIC_SENDINBLUE_KEY}"
                };
                /* OPTIONAL: email for identify request*/
                // window.sib.email_id = 'example@domain.com';
                window.sendinblue = {};
                for (var j = ['track', 'identify', 'trackLink', 'page'], i = 0; i < j.length; i++) {
                (function(k) {
                    window.sendinblue[k] = function() {
                        var arg = Array.prototype.slice.call(arguments);
                        (window.sib[k] || function() {
                                var t = {};
                                t[k] = arg;
                                window.sib.equeue.push(t);
                            })(arg[0], arg[1], arg[2], arg[3]);
                        };
                    })(j[i]);
                }
                var n = document.createElement("script"),
                    i = document.getElementsByTagName("script")[0];
                n.type = "text/javascript", n.id = "sendinblue-js", n.async = !0, n.src = "https://sibautomation.com/sa.js?key=" + window.sib.client_key, i.parentNode.insertBefore(n, i), window.sendinblue.page();
            })();`,
            }}
          />
          <PostHogProvider client={posthog}>
            <Component
              {...pageProps}
              key={`${router.route}-${router.locale}`}
            />
          </PostHogProvider>
        </ChakraProvider>
      </IntlProvider>
    </AppErrorBoundary>
  );
}

export default MyApp;

export function reportWebVitals(metric) {
  if (typeof window !== "undefined" && posthog) {
    posthog.capture("web_vitals", {
      metric_name: metric.name,
      metric_value: Math.round(
        metric.name === "CLS" ? metric.value * 1000 : metric.value,
      ),
      metric_rating: metric.rating || null,
      metric_id: metric.id,
    });
  }
}
